import { useState, type ChangeEvent, type FocusEvent } from 'react';
import { Campo, type PropsCampo } from './Campo';
import {
  erroDoDocumento,
  erroDoTelefone,
  mascararCnpj,
  mascararCpf,
  mascararCpfOuCnpj,
  mascararTelefone,
  type TipoDeDocumento,
} from '../../servicos/documento';

/**
 * Campos de CPF, CNPJ e telefone — máscara enquanto digita, validação ao sair.
 *
 * ## Um componente por documento, e não uma prop `mascara`
 *
 * O que muda entre eles não é só o formato: é quando acusar erro e com que
 * mensagem. Um CNPJ com 14 dígitos errados é inválido na hora; um com 10 só
 * está incompleto — e só vira erro quando a pessoa sai do campo. Com uma prop
 * genérica cada tela reimplementaria essa dança.
 *
 * ## `onChange` recebe o valor mascarado
 *
 * O formulário guarda o texto como está na tela. O servidor limpa a máscara;
 * o front não precisa carregar duas versões de cada campo.
 *
 * ## `erro` externo vence
 *
 * Quem usa pode passar `erro` (um "CPF já cadastrado" que veio do servidor).
 * Quando vier, é ele que aparece — a validação local só fala quando ninguém
 * mais tem nada a dizer.
 */

interface PropsBase extends Omit<
  PropsCampo,
  'onChange' | 'value' | 'inputMode' | 'type' | 'maxLength'
> {
  readonly value: string;
  readonly onChange: (valor: string) => void;
}

function CampoDeDocumento(props: PropsBase & { readonly tipo: TipoDeDocumento }): JSX.Element {
  const { tipo, value, onChange, onBlur, erro, ...resto } = props;
  const [saiu, setSaiu] = useState(false);
  const mascarar =
    tipo === 'cpf' ? mascararCpf : tipo === 'cnpj' ? mascararCnpj : mascararCpfOuCnpj;

  return (
    <Campo
      {...resto}
      inputMode="numeric"
      autoComplete="off"
      value={mascarar(value)}
      erro={erro ?? erroDoDocumento(value, tipo, saiu)}
      onChange={(ev: ChangeEvent<HTMLInputElement>) => {
        setSaiu(false);
        onChange(mascarar(ev.target.value));
      }}
      onBlur={(ev: FocusEvent<HTMLInputElement>) => {
        setSaiu(true);
        onBlur?.(ev);
      }}
    />
  );
}

export function CampoCpf(props: PropsBase): JSX.Element {
  return (
    <CampoDeDocumento {...props} tipo="cpf" placeholder={props.placeholder ?? '000.000.000-00'} />
  );
}

export function CampoCnpj(props: PropsBase): JSX.Element {
  return (
    <CampoDeDocumento
      {...props}
      tipo="cnpj"
      placeholder={props.placeholder ?? '00.000.000/0000-00'}
    />
  );
}

export function CampoCpfOuCnpj(props: PropsBase): JSX.Element {
  return (
    <CampoDeDocumento
      {...props}
      tipo="cpf-ou-cnpj"
      placeholder={props.placeholder ?? 'CPF ou CNPJ'}
    />
  );
}

export function CampoTelefone(props: PropsBase): JSX.Element {
  const { value, onChange, onBlur, erro, ...resto } = props;
  const [saiu, setSaiu] = useState(false);

  return (
    <Campo
      {...resto}
      type="tel"
      inputMode="tel"
      autoComplete="tel-national"
      placeholder={props.placeholder ?? '(00) 00000-0000'}
      value={mascararTelefone(value)}
      erro={erro ?? erroDoTelefone(value, saiu)}
      onChange={(ev: ChangeEvent<HTMLInputElement>) => {
        setSaiu(false);
        onChange(mascararTelefone(ev.target.value));
      }}
      onBlur={(ev: FocusEvent<HTMLInputElement>) => {
        setSaiu(true);
        onBlur?.(ev);
      }}
    />
  );
}
